import React from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Shared hero used by the new audience landing pages — /athletes, /parents,
 * /coaches, /schools, /method.
 *
 * Same visual language as the marketing homepage hero (dark charcoal field,
 * Midnight Green accents, premium athletic typography) but compact:
 *   • eyebrow tag
 *   • headline (≤ 2 lines)
 *   • short subhead
 *   • 2 CTAs (primary + secondary)
 *
 * This is intentionally NOT the full HeroWithTabs — these are gateway pages,
 * not the homepage.
 *
 * Phase 22 (information-architecture restructure).
 */
export const AudienceHero = ({
  eyebrow,
  headline,
  subhead,
  primaryCta,
  secondaryCta,
  testIdPrefix = 'audience-hero',
}) => {
  return (
    <section
      className="relative bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white pt-[96px] sm:pt-[120px] pb-16 sm:pb-20 overflow-hidden"
      data-testid={`${testIdPrefix}-section`}
    >
      {/* Subtle diagonal lines on the right — same texture used elsewhere on the site */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-[0.07]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 22px)',
        }}
      />
      <div className="relative max-w-[1120px] mx-auto px-5 sm:px-6">
        {eyebrow && (
          <div
            className="font-mono-accent uppercase tracking-[0.25em] text-[10.5px] text-[#4FBCC7] font-bold mb-5"
            data-testid={`${testIdPrefix}-eyebrow`}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="font-heading text-[2.25rem] sm:text-[3rem] lg:text-[3.5rem] font-bold tracking-tight leading-[1.05] max-w-[22ch] mb-5"
          data-testid={`${testIdPrefix}-headline`}
        >
          {headline}
        </h1>
        {subhead && (
          <p
            className="text-[16.5px] sm:text-[18px] leading-[1.55] text-white/65 max-w-[58ch] mb-9"
            data-testid={`${testIdPrefix}-subhead`}
          >
            {subhead}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {primaryCta && (
            <Button
              asChild
              className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-12 px-6 text-[14.5px] rounded-md active:scale-[0.98] shadow-md"
            >
              <a
                href={primaryCta.href}
                data-testid={`${testIdPrefix}-primary-cta`}
                {...(primaryCta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {primaryCta.label}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
          {secondaryCta && (
            <Button
              asChild
              variant="outline"
              className="h-12 px-6 text-[14.5px] rounded-md font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            >
              <a
                href={secondaryCta.href}
                data-testid={`${testIdPrefix}-secondary-cta`}
                {...(secondaryCta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {secondaryCta.label}
              </a>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AudienceHero;
